import type { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { parseAllowedReturnUrl, readEntryReferrer } from '../../shared/utils/return-origin';
import type { PreferredSailing, Reservation } from '../../shared/services/reservation.service';
import type { EditorReservationRequest } from './editor.service';
import { parseNonNegativeIntParam, parsePositiveIntParam } from '../../shared/utils/int-param';

/**
 * Everything a booking hand-off link can carry into the funnel, parsed once and
 * shared by the landing screen (which creates the reservation) and the editor
 * (which re-reads the same URL on refresh).
 *
 * The funnel is a SEPARATE ORIGIN from the site that links into it. The legacy
 * webshop never needed most of these in the URL because its `createReservation`
 * state read the party straight off the in-SPA `searchCriteria` service
 * (`apps/webshop/src/app/common/search-bar/search-bar.js:56-64`) — same app,
 * same memory. Nothing of that survives a cross-origin hand-off, so whatever the
 * customer chose before clicking "Varaa" either travels in the link or is
 * silently lost.
 */
export interface EntryParams {
  tripId: number | null;
  reservationId: number | null;
  departureDate: string | null;
  extendableHotelPhaseNights: number | null;
  adults: number;
  children: number;
  /** Ages in years, positionally matched to `children`. May be SHORTER than
   *  `children`: a link carrying only a count leaves the rest at the funnel's
   *  default child age. */
  childAgesYears: number[];
  /** Domestic feeder-bus origin city id, or null for "no bus". */
  busOriginCityId: number | null;
  /** Own-car-onto-the-ferry intent. Trips with `requireOwnCar` are forced
   *  car-bound by the backend regardless (`OwnCarResolver.js:75`), so this
   *  carries the choice for the *optional* (`allowOwnCar`) trips. */
  ownCar: boolean;
  /** Travel-step id of a specific hotel to pin into autoreserve — set only when
   *  the customer searched for that hotel by name (legacy `searchedHotelId`). */
  withHotel: number | null;
  /** The ferry an offer link advertises each way (`?outboundShip=&outboundTime=`,
   *  `?inboundShip=&inboundTime=`) — see {@link parseSailingParams}. */
  outboundSailing: PreferredSailing | null;
  inboundSailing: PreferredSailing | null;
  /** Absolute URL to send the top-bar back button to: a site-relative param
   *  already resolved against {@link environment.webshopBaseUrl}, or an
   *  allowlisted absolute one as given. Null when the link carries none — the
   *  fallbacks then apply, see {@link resolveBackUrl}. */
  returnUrl: string | null;
  /** A gift-card code to apply once the reservation exists — the reward shop's
   *  `?giftCard=` — normalised by {@link parseGiftCardParam}, or null. Read once,
   *  at bootstrap, by `shared/utils/gift-card-link.ts`, which also takes it out
   *  of the URL; it is deliberately not part of the reservation request. */
  giftCard: string | null;
  /** A discount code to apply once the reservation exists — a campaign link's
   *  `?discountCode=` — normalised by {@link parseDiscountCodeParam}, or null.
   *  Read once, at bootstrap, by `shared/utils/discount-code-link.ts`, which also
   *  takes it out of the URL; like `giftCard`, not part of the reservation request. */
  discountCode: string | null;
  /** The customer-facing name the link gives the booking (`?altName=`), saved
   *  as `Reservation.altName` and shown in place of the trip's own name — see
   *  {@link parseAltNameParam}. Null when the link names none. */
  altName: string | null;
  /** The reservation's look (`?theme=`), saved as `Reservation.theme` — see
   *  {@link parseThemeParam}. Null when the link names none or an unknown one. */
  theme: ReservationTheme | null;
}

/** How large a party a customer may book for themselves — adults and children
 *  together. One ceiling over the whole booking, not one per traveller type: a
 *  group of thirty is a group of thirty however it splits. */
export const MAX_PARTY = 30;
/** Staff ceiling. ERP agents put group departures together on the phone —
 *  coach loads, works outings, allotment flight groups — and those routinely run
 *  past what the public funnel offers, so the passenger stepper opens wider for
 *  an internal session. See `EditorComponent.openPassengersDialog`. */
export const MAX_PARTY_INTERNAL = 50;

/**
 * Ceiling a customer gets on a trip that flies.
 *
 * Not a policy number of ours: `flightGDS.maxPassengers` in the legacy backend
 * (`apps/legacy-backend/config/development.js`, unchanged in `aws-production.js`)
 * is 9, which is the GDS convention of nine passengers per PNR. Above it
 * `TripGDSFetcher._shouldSkipFlightGDSSearch` *skips the flight search entirely*
 * rather than erroring, so a party of ten gets an empty itinerary list and the
 * funnel's generic "no flights available" dialog — which reads as "not on this
 * date" rather than "too many of you". Capping the stepper is what turns that
 * silent dead end into something the customer can act on.
 *
 * Allotment flights — IMT's own contracted seats — have no backend cap and are
 * how a party of thirteen ever flew. The funnel can't tell the two apart this
 * early, though: `isAllotment` is derived from a fetched itinerary
 * (`flight-step.service.ts`), long after the party is set. So a customer is
 * capped on every flight trip, and a genuine large allotment group goes to the
 * groups desk (`ryhmat@imt.fi`, named in `editor.passengers.maxPartyFlight`).
 *
 * Staff are exempt, and take {@link MAX_PARTY_INTERNAL} here as on any trip:
 * selling allotment seats to a group larger than nine is exactly the booking
 * they need the funnel for, and nothing behind it refuses one — the allotment
 * path has no cap and `passenger-caps.js` lets an internal request book 200.
 * The GDS search is still skipped above nine for staff too, so a date without
 * allotment seats still comes up empty — a dead end an agent can read for what
 * it is, where a customer would take it for "sold out".
 *
 * An entry link is still clamped to this for every session
 * (`partyWithinFlightCap` runs before any session is consulted); an agent
 * raises the party past it in the stepper.
 */
export const MAX_PARTY_FLIGHT = 9;

/** The ceiling actually in force, given what the trip sells and who is asking. */
export function maxPartyFor(options: { hasFlight: boolean; isInternalUser: boolean }): number {
  if (options.isInternalUser) return MAX_PARTY_INTERNAL;
  return options.hasFlight ? MAX_PARTY_FLIGHT : MAX_PARTY;
}

/** The party fields an entry link carries, and which {@link clampEntryParty}
 *  narrows once the trip is known. */
export type EntryParty = Pick<EntryParams, 'adults' | 'children' | 'childAgesYears'>;

/**
 * Fit an entry link's party under `max`, adults first.
 *
 * Clamps rather than falling back to the default party the way {@link parseParty}
 * does for a malformed value: "twelve of us" is a well-formed, deliberate
 * request that the trip merely cannot serve, so the nearest party it *can* serve
 * is a better landing place than two strangers. Adults win the surviving seats
 * because a child needs one — a party of nine adults is bookable, nine children
 * is not.
 *
 * Ages are truncated with the count they belong to, keeping `childAgesYears`
 * positionally matched to `children`.
 */
export function clampEntryParty(party: EntryParty, max: number): EntryParty {
  if (party.adults + party.children <= max) return party;
  const adults = Math.min(party.adults, max);
  const children = Math.min(party.children, max - adults);
  return { adults, children, childAgesYears: party.childAgesYears.slice(0, children) };
}

const DEFAULT_ADULTS = 2;

/** Bounds on a child's age in years. Above this the passenger is an adult as
 *  far as the backend's `distinguishPassengers` is concerned. */
export const MAX_CHILD_AGE_YEARS = 17;

/**
 * The slice of Angular's `ParamMap` this module needs. Declaring it structurally
 * (rather than importing `ParamMap`) means `URLSearchParams` satisfies it too,
 * so the parser is unit-testable without a router harness.
 */
export interface QueryParamReader {
  get(name: string): string | null;
  getAll(name: string): string[];
}

/**
 * Accepts both boolean encodings that exist in the wild: the legacy webshop's
 * UI-Router `bool` (`1`/`0`, `reservation.js:35`) and ERP-UI's `funnelUrl()`
 * (`true`/`false`, `trip-management.module.js:1355`). Returns null when absent
 * or unrecognised so a silent link isn't read as an explicit "no".
 */
export function parseBoolParam(value: string | null | undefined): boolean | null {
  if (value === '1' || value === 'true') return true;
  if (value === '0' || value === 'false') return false;
  return null;
}

function parseBoundedInt(value: string | null, min: number, max: number): number | null {
  if (value == null || value.trim() === '') return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  if (parsed < min || parsed > max) return null;
  return parsed;
}

/** `YYYY-MM-DD`, and a real calendar date. Anything else is treated as absent,
 *  so a mangled link opens the trip dateless instead of failing autoreserve. */
function parseIsoDateParam(value: string | null): string | null {
  if (value == null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  return Number.isNaN(Date.parse(value)) ? null : value;
}

/** Exactly what a browser strips out of a URL before resolving it. Those three
 *  can smuggle a leading `//` past a naive prefix check ("/<TAB>/evil.example"),
 *  so they go before the shape checks below. (Filtered rather than
 *  regex-replaced: a control-character class trips the `no-control-regex` lint
 *  rule.) */
const URL_STRIPPED_CHARS = ['\t', '\n', '\r'];

/**
 * Resolve the `?returnTo=` back-link into an absolute URL to leave the funnel by.
 *
 * TWO FORMS, because the funnel is linked into from more than one origin:
 *
 * - A site-relative path, resolved against {@link environment.webshopBaseUrl}.
 *   This is what `www.imt.fi` emits (`modern-webshop/.../booking-url.util.ts`),
 *   and it stays the precise form: it carries the page AND its search criteria.
 * - An absolute `https:` URL on an allowlisted host. Campaign subdomains
 *   (`ruotsiottelu.imt.fi`, `golf.imt.fi`, …) cannot express themselves as a
 *   path off `www`, and the set of them grows per campaign — an allowlist of
 *   registrable domains means a new one needs no funnel deploy.
 *
 * This value ends up in an `<a href>` the customer clicks, so an unvetted
 * absolute URL would make the funnel an open redirect:
 * `varaus.imt.fi/?trip=1&returnTo=https://evil.example` is a phishing-grade link
 * with our own domain in front of it. The path form is safe by construction (the
 * result is always `webshopBaseUrl + path`); the absolute form is safe by the
 * scheme/credential/host checks in {@link parseAllowedReturnUrl}, which is
 * shared with the referrer fallback so the two cannot drift on what they trust.
 *
 * The caller's param map has already percent-decoded the value, so this must NOT
 * decode again — a second pass would turn `%2F%2Fevil` back into a bypass.
 */
export function parseReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Trimmed first so a link mangled with leading whitespace still resolves;
  // interior spaces are left alone, since a return path legitimately carries
  // them inside a decoded query value ("?q=hotelli tampere").
  const value = [...raw.trim()].filter((ch) => !URL_STRIPPED_CHARS.includes(ch)).join('');

  if (value.startsWith('/')) {
    if (value.startsWith('//') || value.startsWith('/\\')) return null;
    const base = environment.webshopBaseUrl.replace(/\/$/, '');
    return `${base}${value}`;
  }
  return parseAllowedReturnUrl(value);
}

/**
 * The party the link carries, or the default party when it carries one the
 * funnel could not then represent.
 *
 * Bounded by the *customer* ceiling even though a staff session may go higher.
 * A link is not a session — anyone can hand-edit `?adults=`, so honouring the
 * staff ceiling here would hand every customer a booking past the cap they are
 * held to, without their ever opening the stepper that would clamp it. And no
 * link legitimately carries more: ERP-UI's `funnelUrl()`
 * (`trip-management.module.js:1394`) carries no party at all, and a staff agent
 * who needs forty travellers types the number into the stepper, which is where
 * {@link MAX_PARTY_INTERNAL} applies.
 *
 * Out-of-range values fall back to the default rather than being pinned to the
 * cap, so a mangled link opens on a party the customer recognises as not theirs
 * instead of one that looks deliberate.
 */
function parseParty(
  params: QueryParamReader,
): Pick<EntryParams, 'adults' | 'children' | 'childAgesYears'> {
  const childAgesYears = params
    .getAll('childOfAge')
    .map((age) => parseBoundedInt(age, 0, MAX_CHILD_AGE_YEARS))
    .filter((age): age is number => age !== null)
    // One adult always travels, so the children alone can never fill the party.
    .slice(0, MAX_PARTY - 1);

  // Ages are the richer signal, so they set the count when present; `children`
  // alone still works for links that only know how many (ERP-UI, hand-written).
  const children =
    childAgesYears.length > 0
      ? childAgesYears.length
      : (parseBoundedInt(params.get('children'), 0, MAX_PARTY - 1) ?? 0);

  const adults = parseBoundedInt(params.get('adults'), 1, MAX_PARTY) ?? DEFAULT_ADULTS;

  // Each half is in range and the two together are not — the cap is on the
  // party, so this link is as unrepresentable as an out-of-range count.
  if (adults + children > MAX_PARTY) {
    return { adults: DEFAULT_ADULTS, children: 0, childAgesYears: [] };
  }
  return { adults, children, childAgesYears };
}

/** Longest gift-card code a link may carry. Generous against the codes in use,
 *  and still a cap: nothing legitimate sends a page of text in this param. */
export const MAX_GIFT_CARD_CODE_LENGTH = 32;

/** A gift-card code as the backend looks it up: trimmed and upper-cased, since a
 *  hand-edited or copy-pasted link carries it in whatever case. Null when absent,
 *  blank, or longer than {@link MAX_GIFT_CARD_CODE_LENGTH}. */
export function parseGiftCardParam(raw: string | null | undefined): string | null {
  const code = raw?.trim().toUpperCase() ?? '';
  if (code === '' || code.length > MAX_GIFT_CARD_CODE_LENGTH) return null;
  return code;
}

/** Longest discount code a link may carry. Generous against the codes in use,
 *  and still a cap: nothing legitimate sends a page of text in this param. */
export const MAX_DISCOUNT_CODE_LENGTH = 64;

/** A discount code as the backend looks it up (`reservation-api-public.js`,
 *  `addDiscountCode`): upper-cased, with every space taken out. Null when
 *  absent, blank, or longer than {@link MAX_DISCOUNT_CODE_LENGTH}. */
export function parseDiscountCodeParam(raw: string | null | undefined): string | null {
  const code = raw?.toUpperCase().replace(/\s/g, '') ?? '';
  if (code === '' || code.length > MAX_DISCOUNT_CODE_LENGTH) return null;
  return code;
}

/** `Reservation.altName` is a `VARCHAR(120)`. */
export const MAX_ALT_NAME_LENGTH = 120;

/** A link's `?altName=` as legacy's `createReservation` saved it: trimmed and
 *  cut to {@link MAX_ALT_NAME_LENGTH}, null when absent or blank. Cut rather
 *  than rejected, so an over-long name still opens the trip. */
export function parseAltNameParam(raw: string | null | undefined): string | null {
  const name = raw?.trim() ?? '';
  return name === '' ? null : name.slice(0, MAX_ALT_NAME_LENGTH);
}

/** `Reservation.theme` values — `shared/shared-with-erp/reservation-themes.js`. */
export const RESERVATION_THEMES = ['default', 'pink'] as const;
export type ReservationTheme = (typeof RESERVATION_THEMES)[number];

/**
 * A link's `?theme=`, or null when absent or not one the backend accepts. An
 * unknown value is dropped rather than sent on: `POST /webshop/reservations`
 * answers it with a 400, which would turn a mistyped campaign link into a trip
 * that does not open. (Legacy's `getResolvedTheme` resolved it to `default`,
 * which is what the backend stores for a missing one.)
 *
 * Nothing in the funnel is coloured by it. The legacy `pink` theme swaps the
 * legacy flow's lilac for the pink palette the funnel already wears; it is
 * saved so the reservation keeps it wherever else it is shown (ERP, the
 * legacy reservation pages).
 */
export function parseThemeParam(raw: string | null | undefined): ReservationTheme | null {
  const theme = raw?.trim() ?? '';
  return (RESERVATION_THEMES as readonly string[]).includes(theme)
    ? (theme as ReservationTheme)
    : null;
}

/**
 * One leg of an offer link's ferry: `?<leg>Ship=` (a ship travel-step id) and
 * `?<leg>Time=` (`HH:mm` on the departure port's clock; `9:30` reads as
 * `09:30`). Null unless both are there and readable: the two only mean
 * something together, and a mangled link should still open the trip.
 */
export function parseSailingParams(
  params: QueryParamReader,
  leg: 'outbound' | 'inbound',
): PreferredSailing | null {
  const shipId = parsePositiveIntParam(params.get(`${leg}Ship`));
  const time = /^(\d{1,2}):(\d{2})$/.exec(params.get(`${leg}Time`)?.trim() ?? '');
  if (shipId == null || !time) return null;
  const hours = Number(time[1]);
  if (hours > 23 || Number(time[2]) > 59) return null;
  return { shipId, localTime: `${String(hours).padStart(2, '0')}:${time[2]}` };
}

/** Parse a booking hand-off URL. Never throws: every malformed value degrades to
 *  its default so a mangled link still opens the trip. */
export function parseEntryParams(params: QueryParamReader): EntryParams {
  return {
    // `tripId` is the alias ERP-UI's deep links use; `trip` is the webshop's.
    tripId: parsePositiveIntParam(params.get('trip') ?? params.get('tripId')),
    reservationId: parsePositiveIntParam(params.get('reservation')),
    departureDate: parseIsoDateParam(params.get('departureDate')),
    // `0` extra hotel nights is a real request, not a malformed one.
    extendableHotelPhaseNights: parseNonNegativeIntParam(params.get('extendableHotelPhaseNights')),
    ...parseParty(params),
    busOriginCityId: parsePositiveIntParam(params.get('originCity')),
    ownCar: parseBoolParam(params.get('ownCar')) ?? false,
    withHotel: parsePositiveIntParam(params.get('withHotel')),
    outboundSailing: parseSailingParams(params, 'outbound'),
    inboundSailing: parseSailingParams(params, 'inbound'),
    returnUrl: parseReturnUrl(params.get('returnTo')),
    giftCard: parseGiftCardParam(params.get('giftCard')),
    discountCode: parseDiscountCodeParam(params.get('discountCode')),
    altName: parseAltNameParam(params.get('altName')),
    theme: parseThemeParam(params.get('theme')),
  };
}

/**
 * Turn an entry link into the reservation request the editor query is keyed on,
 * or `{ tripId: null }` when the link names no usable trip (the caller then
 * routes to the pick-a-trip screen).
 *
 * THIS LIVES HERE, NOT IN THE TWO CALLERS, DELIBERATELY. The landing screen and
 * the editor both read the same URL and both feed `editorReservationOptions`, whose
 * query key is built from every field below. If the two ever disagreed about a
 * field — say one adds a param the other doesn't — the keys would differ and the
 * editor would create a SECOND reservation the moment the landing screen
 * forwarded to it. Two hand-synced copies are exactly how that drift happens, so
 * there is one copy.
 */
export function toEditorReservationRequest(
  params: QueryParamReader,
): EditorReservationRequest | { tripId: null } {
  const entry = parseEntryParams(params);
  if (entry.tripId == null) {
    return { tripId: null };
  }
  return {
    tripId: entry.tripId,
    reservationId: entry.reservationId,
    departureDate: entry.departureDate,
    extendableHotelPhaseNights: entry.extendableHotelPhaseNights,
    adults: entry.adults,
    children: entry.children,
    childAgesYears: entry.childAgesYears,
    busOriginCityId: entry.busOriginCityId,
    ownCar: entry.ownCar,
    withHotel: entry.withHotel,
    outboundSailing: entry.outboundSailing,
    inboundSailing: entry.inboundSailing,
    altName: entry.altName,
    theme: entry.theme,
  };
}

/**
 * The hotel every autoreserve should pin: the entry link's `?withHotel=`, for as
 * long as the customer has not moved off it. Null when the link names none, or
 * when the reservation holds hotels and the link's is not among them — the
 * customer picked another one in the hotel step (or the link's could not be had
 * on the dates they moved to), and that choice is theirs to keep.
 *
 * This is what makes an offer link ("1 yö laivalla + 1 yö Metropolissa,
 * 23.–25.11.") hold together past the first screen. The link's hotel is applied
 * to the reservation it creates, but every later date, length or party change
 * re-runs autoreserve from scratch, and without `withHotel` that resolves the
 * hotel phase to the trip's default — quietly swapping the advertised hotel for
 * another. The legacy webshop kept the link's hotel for the same reason
 * (`departure.js`, `getPreselectedHotelId()` on every date and length change).
 *
 * A reservation holding no hotel at all — dateless, or entered on a date the
 * hotel was full — still pins it, so the first date picked lands on it.
 *
 * Derived from the reservation rather than remembered, so a reload, a resumed
 * reservation and the hotel step all agree without any state of their own.
 */
export function pinnedLinkHotelId(
  linkHotelId: number | null,
  reservation: Reservation | null | undefined,
): number | null {
  if (linkHotelId == null) return null;
  const heldHotelIds = (reservation?.reservedProducts ?? [])
    .map((rp) => rp.product?.resource?.travelStep)
    .filter((travelStep) => travelStep?.type === 'Hotel')
    .map((travelStep) => travelStep?.id);
  return heldHotelIds.length === 0 || heldHotelIds.includes(linkHotelId) ? linkHotelId : null;
}

/** The entry link's ferry each way — see {@link parseSailingParams}. */
export interface LinkSailings {
  outboundSailing: PreferredSailing | null;
  inboundSailing: PreferredSailing | null;
}

/**
 * The ferries every autoreserve should still prefer: the entry link's, minus any
 * leg where the customer has confirmed another crossing in the ferry sheet
 * (`choseOther`). That choice is theirs to keep, as picking another hotel is for
 * {@link pinnedLinkHotelId}.
 *
 * Not judged by the sailing the reservation holds alone, unlike the hotel. A
 * ferry is a dated sailing: the link's may not sail on a date the customer passes
 * through, and autoreserve then quietly takes another. Ending the pin on that
 * would lose the advertised ferry for good, even after they move back to a date
 * it sails on. A hotel stands on every date.
 *
 * The backend treats these as preferences, so a sailing that cannot be had on the
 * new dates resolves as if it had not been named.
 */
export function pinnedLinkSailings(
  link: LinkSailings,
  choseOther: { outbound: boolean; inbound: boolean },
): LinkSailings {
  return {
    outboundSailing: choseOther.outbound ? null : link.outboundSailing,
    inboundSailing: choseOther.inbound ? null : link.inboundSailing,
  };
}

/**
 * Where a "leave the funnel" control should point. Shared by the landing
 * screen's CTAs and the editor's top-bar back button.
 *
 * Three sources, most trustworthy first:
 *
 * 1. The link's own `?returnTo=`. Deliberate, and the only one that can name a
 *    specific page with the criteria the customer had.
 * 2. The referrer captured when they entered the funnel
 *    (`shared/utils/return-origin.ts`). A guess, and usually origin-only, but it
 *    gets a campaign site the right back button with no change on its side.
 * 3. The content site's front page — right for nobody in particular, wrong for
 *    nobody either.
 */
export function resolveBackUrl(params: QueryParamReader | null | undefined): string {
  return (
    (params ? parseEntryParams(params).returnUrl : null) ??
    readEntryReferrer() ??
    environment.webshopBaseUrl
  );
}

/**
 * Re-enter the current screen on the same trip with no departure date.
 *
 * The date-less entry is a shape the loader already supports —
 * `resolveEditorReservation` seeds the party and skips autoreserve entirely —
 * so this lands on the hub with its date row empty and highlighted, and the day
 * picker (which paints only the days the trip can actually be sold on) one tap
 * away.
 *
 * Dropping `departureDate` changes the query key, so it is a fresh load rather
 * than a retry of the one that just failed; it resumes the reservation that
 * attempt already created (same trip, still `Initial`), so nothing is orphaned
 * behind it. Shared by the landing screen's no-availability card and the
 * editor's entry-error dialog, which offer the customer the same way out.
 */
export function clearDepartureDateParam(router: Router): Promise<boolean> {
  return router.navigate([], {
    queryParams: { departureDate: null },
    queryParamsHandling: 'merge',
  });
}
